import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-3.5 font-medium transition-colors active:scale-[0.97] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-56 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-fg-default text-bg-base border border-transparent hover:opacity-90 font-semibold",
        secondary:
          "bg-bg-elevated text-fg-muted border border-border-default hover:text-fg-default hover:border-fg-subtle cursor-pointer",
        gold: "bg-gold-molten-wash-hover text-gold-ink border border-gold-line-32 hover:text-gold-ink hover:bg-gold-molten-wash-active hover:border-gold-line-48 cursor-pointer",
        icon: "bg-bg-muted text-fg-muted hover:border-[var(--border-strong,var(--border-default))] hover:text-fg-default border border-border-default",
        ghost:
          "text-fg-muted hover:text-fg-default hover:bg-bg-muted data-[state=open]:text-fg-default data-[state=open]:bg-bg-muted",
        link: "text-primary underline-offset-4 hover:underline",
        danger:
          "bg-agent-resume-page text-status-error border border-transparent hover:border-status-error",
        dropdown:
          "group/trigger gap-1.5 justify-between text-fg-muted bg-bg-muted border border-border-subtle whitespace-nowrap hover:bg-bg-elevated hover:text-fg-default hover:border-border-default data-[state=open]:bg-bg-elevated data-[state=open]:text-fg-default data-[state=open]:border-accent-primary",
        unstyled: "",
      },
      size: {
        default: "px-4 py-2.5 min-h-[42px] rounded-10",
        sm: "px-3 py-2 rounded-10",
        xs: "px-2.5 py-1.5 rounded-8 text-3 xs:text-3.5",
        xxs: "px-2 py-1 rounded-8 text-3",
        "xxs-icon": "px-2 py-1 min-w-9 h-7 rounded-12 text-3 font-medium",
        icon: "h-9 w-9 rounded-8",
        "icon-md": "h-7.5 w-7.5 rounded-7",
        "icon-sm": "h-6 w-6 rounded-6",
        "icon-xs": "h-4 w-4 rounded-4",
        unstyled: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
